import { motion } from 'framer-motion';
import { useTheme } from './theme';

// Custom typographic-geometric thumbnails per case study.
// Each slug maps to a colour story + oversized glyph + a decorative shape.
// Purely SVG/CSS — no external images, impossible to 404.
//
// Each theme has a saturated dark variant (default) and a muted, low-saturation
// light variant so the thumbnails harmonise with the cream + forest editorial
// palette instead of shouting at it.

const THEMES = {
    'beyond-the-dashboard': {
        no: '09',
        label: 'Intelligent\nTrucks',
        tagline: 'Connected truck HMI · concept',
        bg: 'linear-gradient(135deg, #05140a 0%, #0f2e18 55%, #26401a 100%)',
        accent: '#D8FE5B',
        ink: '#ffffff',
        bgLight: 'linear-gradient(135deg, #AFC59C 0%, #4C6B3E 100%)',
        accentLight: '#5F7A2E',
        inkLight: '#FBF2EA',
        shape: 'pulse',
    },
    'homepage-redesign': {
        no: '01',
        label: 'Homepage',
        tagline: 'Engagedly redesign',
        bg: 'linear-gradient(135deg, #0b1e3d 0%, #0ea5e9 100%)',
        accent: '#facc15',
        ink: '#ffffff',
        bgLight: 'linear-gradient(135deg, #B7CAD8 0%, #486A88 100%)',
        accentLight: '#C8853A',
        inkLight: '#FBF2EA',
        shape: 'grid',
    },
    'my-communication': {
        no: '02',
        label: 'My Comms',
        tagline: 'The Cluttered Clarity',
        bg: 'linear-gradient(135deg, #3f0764 0%, #d946ef 100%)',
        accent: '#f472b6',
        ink: '#ffffff',
        bgLight: 'linear-gradient(135deg, #C7A0BE 0%, #6C3F72 100%)',
        accentLight: '#E88BAE',
        inkLight: '#FBF2EA',
        shape: 'rings',
    },
    'cloudhub-enterprise-redesign': {
        no: '03',
        label: 'CloudHub',
        tagline: 'Multi-cloud SaaS',
        bg: 'linear-gradient(135deg, #061428 0%, #1e40af 60%, #0891b2 100%)',
        accent: '#67e8f9',
        ink: '#ffffff',
        bgLight: 'linear-gradient(135deg, #94B4C7 0%, #305978 55%, #3A7A88 100%)',
        accentLight: '#D9B77A',
        inkLight: '#FBF2EA',
        shape: 'orbit',
    },
    'symptom-tracking': {
        no: '04',
        label: 'Symptom',
        tagline: 'Health tracking app',
        bg: 'linear-gradient(135deg, #052e2b 0%, #10b981 100%)',
        accent: '#a7f3d0',
        ink: '#ffffff',
        bgLight: 'linear-gradient(135deg, #9DBEA9 0%, #365B48 100%)',
        accentLight: '#D9A868',
        inkLight: '#FBF2EA',
        shape: 'pulse',
    },
    'travel-app': {
        no: '05',
        label: 'Travel',
        tagline: 'App for visitors',
        bg: 'linear-gradient(135deg, #3b1006 0%, #f97316 100%)',
        accent: '#fde68a',
        ink: '#ffffff',
        bgLight: 'linear-gradient(135deg, #D89767 0%, #96421A 100%)',
        accentLight: '#F0D189',
        inkLight: '#FBF2EA',
        shape: 'arc',
    },
    'graphic-designs': {
        no: '06',
        label: 'Graphic',
        tagline: 'Design collection',
        bg: 'linear-gradient(135deg, #0a0a0a 0%, #404040 100%)',
        accent: '#c6f234',
        ink: '#ffffff',
        bgLight: 'linear-gradient(135deg, #8E8983 0%, #38352F 100%)',
        accentLight: '#D4C868',
        inkLight: '#FBF2EA',
        shape: 'blocks',
    },
    'portal-for-site-contractors': {
        no: '07',
        label: 'Portal',
        tagline: 'For site contractors',
        bg: 'linear-gradient(135deg, #422006 0%, #ca8a04 100%)',
        accent: '#fef08a',
        ink: '#ffffff',
        bgLight: 'linear-gradient(135deg, #CAA36C 0%, #6E4E1A 100%)',
        accentLight: '#F1DB8C',
        inkLight: '#FBF2EA',
        shape: 'stripes',
    },
    'web-template': {
        no: '08',
        label: 'IDP',
        tagline: 'Development plan',
        bg: 'linear-gradient(135deg, #3b0764 0%, #a855f7 100%)',
        accent: '#f0abfc',
        ink: '#ffffff',
        bgLight: 'linear-gradient(135deg, #A88CBE 0%, #4A346C 100%)',
        accentLight: '#DDB88C',
        inkLight: '#FBF2EA',
        shape: 'orbit',
    },
};

// ── Decorative SVG shapes ────────────────────────────────────────────
const Shape = ({ kind, accent }) => {
    if (kind === 'grid')
        return (
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full opacity-25" preserveAspectRatio="xMidYMid slice">
                <defs>
                    <pattern id="g-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                        <path d="M 20 0 L 0 0 0 20" fill="none" stroke={accent} strokeWidth="0.5" />
                    </pattern>
                </defs>
                <rect width="200" height="200" fill="url(#g-grid)" />
                <circle cx="160" cy="40" r="46" fill="none" stroke={accent} strokeWidth="1" opacity="0.7" />
            </svg>
        );
    if (kind === 'rings')
        return (
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full opacity-30" preserveAspectRatio="xMidYMid slice">
                {[20, 40, 60, 80, 100].map((r) => (
                    <circle key={r} cx="160" cy="40" r={r} fill="none" stroke={accent} strokeWidth="0.7" />
                ))}
            </svg>
        );
    if (kind === 'pulse')
        return (
            <svg viewBox="0 0 200 100" className="absolute inset-x-0 bottom-6 w-full h-24 opacity-70" preserveAspectRatio="xMidYMid slice">
                <path
                    d="M 0 50 L 40 50 L 55 30 L 70 70 L 85 20 L 100 80 L 115 50 L 200 50"
                    fill="none"
                    stroke={accent}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    if (kind === 'arc')
        return (
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full opacity-35" preserveAspectRatio="xMidYMid slice">
                <path
                    d="M -20 200 Q 100 -30 220 200"
                    fill="none"
                    stroke={accent}
                    strokeWidth="1.4"
                />
                <path
                    d="M -20 210 Q 100 20 220 210"
                    fill="none"
                    stroke={accent}
                    strokeWidth="0.7"
                    opacity="0.6"
                />
                <circle cx="150" cy="60" r="4" fill={accent} />
            </svg>
        );
    if (kind === 'blocks')
        return (
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full opacity-45" preserveAspectRatio="xMidYMid slice">
                <rect x="10" y="10" width="30" height="30" fill={accent} opacity="0.9" />
                <rect x="50" y="10" width="60" height="30" fill="none" stroke={accent} strokeWidth="1" />
                <rect x="10" y="50" width="60" height="60" fill="none" stroke={accent} strokeWidth="1" />
                <rect x="80" y="50" width="30" height="30" fill={accent} opacity="0.5" />
                <circle cx="155" cy="45" r="18" fill="none" stroke={accent} strokeWidth="1" />
            </svg>
        );
    if (kind === 'stripes')
        return (
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full opacity-30" preserveAspectRatio="xMidYMid slice">
                <defs>
                    <pattern id="g-stripes" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <line x1="0" y1="0" x2="0" y2="12" stroke={accent} strokeWidth="1.5" />
                    </pattern>
                </defs>
                <rect x="120" y="0" width="80" height="200" fill="url(#g-stripes)" />
            </svg>
        );
    if (kind === 'orbit')
        return (
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full opacity-30" preserveAspectRatio="xMidYMid slice">
                <ellipse cx="150" cy="55" rx="50" ry="20" fill="none" stroke={accent} strokeWidth="1" transform="rotate(-15 150 55)" />
                <ellipse cx="150" cy="55" rx="72" ry="30" fill="none" stroke={accent} strokeWidth="0.8" transform="rotate(-15 150 55)" />
                <circle cx="180" cy="45" r="3" fill={accent} />
                <circle cx="115" cy="65" r="2" fill={accent} opacity="0.6" />
            </svg>
        );
    return null;
};

// ── Main thumbnail ───────────────────────────────────────────────────
export const CaseStudyThumb = ({ slug, hover = true, showTagline = true, showMeta = true, className = '' }) => {
    // Pick the muted light-mode variant when the site is in light theme so the
    // saturated dark gradients don't shout at the cream + forest palette.
    const { theme } = useTheme();
    const base = THEMES[slug];
    if (!base) return null;
    const t =
        theme === 'light' && base.bgLight
            ? {
                  ...base,
                  bg: base.bgLight,
                  accent: base.accentLight || base.accent,
                  ink: base.inkLight || base.ink,
              }
            : base;
    return (
        <div
            className={`relative overflow-hidden ${className}`}
            style={{ background: t.bg, color: t.ink }}
            data-testid={`case-study-thumb-${slug}`}
        >
            {/* Decorative shape (SVG) */}
            <Shape kind={t.shape} accent={t.accent} />

            {/* Grain overlay for depth */}
            <div
                className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")",
                }}
            />

            {/* Content — layered on top */}
            <div className="relative w-full h-full flex flex-col justify-between p-4 md:p-5">
                {/* top row — meta chip + accent dot. Suppressed when the parent
                     wants to overlay its own chip (e.g. the Hero spotlight or the
                     Selected Work grid) so we don't get two chips fighting for the
                     same corner. */}
                {showMeta && (
                    <div className="flex items-start justify-between gap-2">
                        <span
                            className="font-mono-brand text-[10px] uppercase tracking-[0.22em] tabular-nums px-2 py-0.5 rounded-full backdrop-blur"
                            style={{
                                background: 'rgba(0,0,0,0.35)',
                                color: t.accent,
                            }}
                        >
                            {t.no} · Case study
                        </span>
                        <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: t.accent, boxShadow: `0 0 12px ${t.accent}` }}
                        />
                    </div>
                )}
                {!showMeta && <span />}

                {/* centrepiece — oversized label */}
                <motion.div
                    className="pointer-events-none"
                    animate={hover ? { y: [0, -2, 0] } : {}}
                    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                >
                    <div
                        className="font-display-italic leading-[0.85] tracking-tight whitespace-pre-line text-[clamp(2.2rem,5.6vw,4rem)]"
                        style={{ color: t.ink }}
                    >
                        {t.label}
                        <span style={{ color: t.accent }}>.</span>
                    </div>
                </motion.div>

                {/* bottom row */}
                <div className="flex items-end justify-between gap-3">
                    {showTagline && (
                        <span
                            className="font-sans-brand text-[11px] leading-[1.35] opacity-80 max-w-[70%]"
                            style={{ color: t.ink }}
                        >
                            {t.tagline}
                        </span>
                    )}
                    <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={t.accent}
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                    >
                        <line x1="7" y1="17" x2="17" y2="7" />
                        <polyline points="7 7 17 7 17 17" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default CaseStudyThumb;
